import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Modal,
  Preloader,
  DatePicker,
  TimeDropdown,
  Tooltip,
  InfoIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { AuthContext, PermissionHOC } from 'auth';
import { ClientSearch, HostsField } from 'components';
import { LocalizationContext } from 'localization';
import {
  BOOKING_FORM_DATA,
  BookingDataResponse,
  CREATE_BOOKING,
  GET_BOOKING,
  ICreateBookingRequest,
  ICreateBookingResponse,
  IGetBookingRequest,
  IGetBookingResponse,
  IUpdateBookingRequest,
  IUpdateBookingResponse,
  UPDATE_BOOKING,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notificationService from 'services/notificationService';
import { BookingHost, EBookingStatus } from 'types';

import s from './BookingModal.module.scss';
import { DEFAULT_VALUES } from './constants';
import { Preview } from './Preview';
import { getBookingRefetchQueries } from './refetchQueries';
import { IFormValues, IBookingModalProps, ITimeDropdownRef } from './types';
import { clockOptions, timeOptions } from './utils';

export const BookingModal: FC<IBookingModalProps> = ({
  onClose,
  selectedHostsIds,
  bookingId,
  refetchQueries = [],
  setIsDefaultClient,
  setIsDepositOpen,
  selectedDate,
  selectedTime,
  isHardBooking,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { setValue } = useForm();
  const { pathname } = useLocation();

  const formData = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_VALUES,
      hostsIds: selectedHostsIds ?? [],
      date: selectedDate ?? DEFAULT_VALUES.date,
      time: selectedTime ?? DEFAULT_VALUES.time,
    },
  });
  const timeDropdownRef = useRef<ITimeDropdownRef | null>(null);

  const getAvailableTimeOptions = (
    selectedDate: string | null
  ): Array<{ value: string; title: string }> => {
    const now = dayjs();
    const isToday = selectedDate && dayjs(selectedDate).isSame(now, 'day');

    return timeOptions.filter(({ value }) => {
      if (!isToday) return true;
      const [hours, minutes] = value.split(':').map(Number);
      const optionTime = dayjs().hour(hours).minute(minutes);
      return optionTime.isAfter(now);
    });
  };

  const handleDateChange = (newDate: string | null): void => {
    setValue('date', newDate);
  };

  const { control, handleSubmit, reset, watch } = formData;
  const date = watch('date');
  const availableTimeOptions = useMemo(
    () => getAvailableTimeOptions(date),
    [date]
  );

  const bookingRefetchQueries = getBookingRefetchQueries(
    pathname,
    refetchQueries
  );

  const [createBooking] = useMutation<
    ICreateBookingResponse,
    ICreateBookingRequest
  >(CREATE_BOOKING, {
    refetchQueries: bookingRefetchQueries,
    awaitRefetchQueries: true,
    onCompleted: () => {
      notificationService.info({
        t,
        dismiss: 5,
        type: 'success',
        title: t('booking.notify.created'),
      });

      onClose();
    },
    onError: (error) => {
      if (error) {
        notificationService.info({
          t,
          dismiss: 5,
          type: 'error',
          title: t('booking.notify.error'),
          message:
            error.message === 'host is booked'
              ? t('booking.notify.error.booked')
              : undefined,
        });
      }
    },
  });

  const [updateBooking, { loading: isUpdating }] = useMutation<
    IUpdateBookingResponse,
    IUpdateBookingRequest
  >(UPDATE_BOOKING, {
    refetchQueries: bookingRefetchQueries,
    awaitRefetchQueries: true,
    onCompleted: ({ updateBooking }) => {
      notificationService.info({
        t,
        dismiss: 5,
        type: 'success',
        title: t(
          updateBooking.status === EBookingStatus.CANCELED
            ? 'booking.notify.canceled'
            : 'booking.notify.updated'
        ),
      });

      onClose();
    },
    onError: (error) => {
      if (error) {
        notificationService.info({
          t,
          dismiss: 5,
          type: 'error',
          title: t('booking.notify.error'),
          message:
            error.message === 'host is booked'
              ? t('booking.notify.error.booked')
              : undefined,
        });
      }
    },
  });

  const { data: bookingForEdit, loading: isBookingForEditLoading } = useQuery<
    IGetBookingResponse,
    IGetBookingRequest
  >(GET_BOOKING, {
    skip: !bookingId || !permissions?.has('booking_manage'),
    variables: { id: bookingId as number },
    fetchPolicy: 'no-cache',
  });

  const { data: bookingFormData, loading: isBookingFormDataLoading } =
    useQuery<BookingDataResponse>(BOOKING_FORM_DATA, {
      fetchPolicy: 'cache-and-network',
      skip: !permissions?.has('booking_manage'),
    });

  const hostGroupsTitlesById = useMemo(
    () =>
      bookingFormData?.hostGroupsOverview.reduce<Record<number, string>>(
        (acc, group) => {
          acc[group.id] = group.title;

          return acc;
        },
        {}
      ),
    [bookingFormData?.hostGroupsOverview]
  );

  const normalizedHosts = useMemo(
    () =>
      bookingFormData?.hostsOverview.reduce<Record<number, BookingHost>>(
        (acc, host) => {
          acc[host.id] = host;

          return acc;
        },
        {}
      ),
    [bookingFormData?.hostsOverview]
  );

  useEffect(() => {
    if (bookingForEdit) {
      const { from, to, client, hostIds, comment } = bookingForEdit.getBooking;

      const dayJSFrom = dayjs(from);
      const dayJSTo = dayjs(to);

      const newFormState: IFormValues = {
        hostsIds: hostIds.filter((hostId: number) => normalizedHosts?.[hostId]),
        clientUUID: client?.uuid ?? null,
        comment: comment ?? '',
        date: dayJSFrom.format('YYYY-MM-DD'),
        time: dayJSFrom.format('HH:mm'),
        duration: dayJSTo.diff(dayJSFrom, 'minutes') / 60,
      };

      reset(newFormState);
    }
  }, [bookingForEdit, normalizedHosts, reset]);

  useEffect(() => {
    if (selectedDate) {
      setValue('date', selectedDate);
    }
    if (selectedTime) {
      setValue('time', selectedTime);
    }
  }, [selectedDate, selectedTime, setValue]);

  useEffect(() => {
    setTimeout(() => {
      if (timeDropdownRef.current) {
        timeDropdownRef.current?.focusInput();
      }
    }, 0);
  }, []);

  return (
    <Modal
      onClose={() => onClose()}
      className={s.container}
      disableOutsideClose
    >
      <Preloader loading={isBookingForEditLoading}>
        <div className={s.header_block}>
          <span className={s.header}>
            {t(bookingId ? 'booking.update.title' : 'booking.add.title')}
          </span>
          {isHardBooking && (
            <Tooltip
              header={t('workShifts.finish.byManager.notification.title')}
              content={t('bookingModal.hard.booking')}
              contentClassName={s.tooltip}
              position={'bottom-left'}
            >
              <div className={s.helper}>
                <InfoIcon size="s" />
                {t('bookingModal.hard.booking')}
              </div>
            </Tooltip>
          )}
        </div>
        <form
          onSubmit={handleSubmit((values) => {
            if (!values.date) return;
            if (!values.time) return;
            if (!values.duration) return;
            if (!values.hostsIds.length) return;

            const dateWithTime = values.date;
            const newTime = values.time;
            const duration = Number(values.duration);
            // const baseDate = dayjs(dateWithTime);

            const [hours, minutes] = newTime.split(':').map(Number);

            const from = dayjs(dateWithTime)
              .hour(hours)
              .minute(minutes)
              .second(0)
              .format('YYYY-MM-DD HH:mm:ss');
            const to = dayjs(from)
              .add(duration, 'hours')
              .format('YYYY-MM-DD HH:mm:ss');

            const input: ICreateBookingRequest['input'] = {
              from,
              to,
              hosts: values.hostsIds,
            };

            if (values.clientUUID) {
              input.client = values.clientUUID;
            }

            if (values.comment) {
              input.comment = values.comment;
            }

            if (bookingId) {
              return updateBooking({ variables: { id: bookingId, input } });
            }

            return createBooking({ variables: { input } });
          })}
          className={s.form}
        >
          <FormProvider {...formData}>
            <PermissionHOC
              permission="client_info"
              className={s.not_client_search}
            >
              <Controller
                control={control}
                name="clientUUID"
                render={({ field: { value, onChange } }) => (
                  <ClientSearch
                    chosenClientUUID={value}
                    setChosenClientUUID={onChange}
                    disabled={
                      !permissions?.has('client_browse') &&
                      !permissions?.has('client_info')
                    }
                    setIsDefaultClient={setIsDefaultClient}
                    setIsDepositOpen={setIsDepositOpen}
                    withReplenishment
                  />
                )}
              />
            </PermissionHOC>

            <div className={s.column}>
              <div className={s['date-time-container']}>
                <div className={s.calendar_block}>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        date={value}
                        setDate={(newDate) => {
                          onChange(newDate);
                          handleDateChange(newDate);
                        }}
                        firstValidDate={dayjs().format()}
                        title={t('booking.add.date')}
                        lang={lang}
                        variant="secondary"
                        withTime={false}
                      />
                    )}
                    control={control}
                    name="date"
                  />
                </div>

                <div className={s.time_block}>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <TimeDropdown
                        items={availableTimeOptions}
                        value={value}
                        onChange={(value: string) => {
                          onChange(value);
                        }}
                        title={t('booking.start')}
                        placeholder={'00:00'}
                        variant="secondary"
                        time={true}
                        className={s.timeDropdown}
                      />
                    )}
                    control={control}
                    name="time"
                  />
                </div>

                <div className={s.clock_block}>
                  <Controller
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <TimeDropdown
                        items={clockOptions}
                        value={String(value).replace(',', '.')}
                        onChange={(newValue: string) => {
                          if (+newValue > 720) return;
                          onChange(newValue.replace(',', '.'));
                        }}
                        title={t('booking.add.time')}
                        placeholder="3"
                        variant="secondary"
                        time={false}
                        className={cn(
                          s.clockDropdown,
                          error && s.clockDropdown_error
                        )}
                        ref={timeDropdownRef}
                        error={error?.message}
                      />
                    )}
                    control={control}
                    name="duration"
                    rules={{
                      required: t('errors.required'),
                    }}
                  />
                </div>
              </div>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <HostsField
                    value={value}
                    onChange={onChange}
                    loading={isBookingFormDataLoading}
                    hosts={bookingFormData?.hostsOverview}
                    hostGroupsTitlesById={hostGroupsTitlesById}
                    currentBooking={bookingForEdit?.getBooking}
                  />
                )}
                name="hostsIds"
                control={control}
              />
            </div>
            <Preview
              hostGroupsTitlesById={hostGroupsTitlesById}
              normalizedHosts={normalizedHosts}
              currentBooking={bookingForEdit?.getBooking}
              cancelBooking={() => {
                if (!bookingForEdit?.getBooking || !bookingId) return;

                const { client, from, to, hostIds, comment } =
                  bookingForEdit.getBooking;

                const input: IUpdateBookingRequest['input'] = {
                  status: EBookingStatus.CANCELED,
                  from,
                  to,
                  hosts: hostIds,
                  comment,
                };

                if (client) {
                  input.client = client.uuid;
                }

                openConfirmDialog(t, 'remove', {
                  title: t('booking.end.title'),
                  message: t('booking.end.description'),
                  acceptTitle: t('booking.end.submit'),
                  declineTitle: t('booking.end.cancel'),
                }).then(({ status }) => {
                  if (status === 'ACCEPTED') {
                    return updateBooking({
                      variables: {
                        id: bookingId,
                        input,
                      },
                    });
                  }
                });
              }}
              isUpdating={isUpdating}
            />
          </FormProvider>
        </form>
      </Preloader>
    </Modal>
  );
};
